import { useEffect, useState } from 'react';
import { FileText, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ClaimDrawer } from './ClaimDrawer';

type Alert = {
  /** Cuántas hay EN TOTAL en el servidor: la lista viene topada, el largo no es el universo. */
  result_total?: number; claim_id: string; label: string; days: number };

/**
 * BARRA DE COC VENCIDO — CON ESCALADA (pedido de Manuel 2026-08-10, mismo patrón que SmsAlert).
 *
 * El COC es lo último que separa un claim terminado de un claim cobrado. La barra sale arriba de
 * toda la app para el EQUIPO de Claims dueño del claim (no toda la oficina). Cerrarla la calla 5
 * minutos… pero cerrarla SUMA:
 *
 *   1ª a 3ª vez → banner arriba (como siempre).
 *   4ª vez      → ventana en el MEDIO que pide una respuesta: "¿por qué no se está enviando?".
 *                 La respuesta se guarda como FOLLOW-UP del depto Claims (con la huella de quién
 *                 la escribió) y eso re-arma el reloj → la alerta se calla hasta la próxima fecha.
 *
 * La cuenta de escalada vive en sessionStorage por claim: recargar la página no la resetea.
 */
const NIVEL_VENTANA = 4;
const KEY = 'coc:escalada';
const leerEscalada = (): Record<string, number> => {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}') as Record<string, number>; } catch { return {}; }
};
const guardarEscalada = (m: Record<string, number>) => {
  try { sessionStorage.setItem(KEY, JSON.stringify(m)); } catch { /* incógnito */ }
};

export function CocAlertBanner() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [nivel, setNivel] = useState<Record<string, number>>(leerEscalada);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get<{ alerts: Alert[] }>('/api/coc/alerts')
      .then((r) => { setAlerts(r.alerts); setTotal(r.alerts[0]?.result_total ?? r.alerts.length); })
      .catch(() => {});
  };
  useEffect(() => {
    load();
    // Cada 2 minutos: cerrarla dura 5, así que consultar cada 10 haría que volviera tarde y de
    // forma impredecible (a veces a los 5, a veces a los 15).
    const h = setInterval(() => { if (!document.hidden) load(); }, 2 * 60_000);
    const alVolver = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { clearInterval(h); document.removeEventListener('visibilitychange', alVolver); };
  }, []);

  if (alerts.length === 0) return null;
  const a = alerts[0]!;
  const n = nivel[a.claim_id] ?? 1;
  // El total sale del SERVIDOR, no del largo de la lista: la lista viene topada en 25, así que
  // con 40 pendientes "y 24 más" mentía por 15 — y hacia abajo, tranquilizando justo cuando había
  // más trabajo del que decía. `total` se lleva aparte y baja al cerrar una, porque el largo de la
  // lista mezcla dos cosas distintas: las que cerró el usuario y las que el tope nunca trajo.
  const restantes = Math.max(0, total - 1);
  const dismiss = () => {
    setAlerts((prev) => prev.slice(1));
    setTotal((x) => Math.max(0, x - 1));
    // Cerrar SUBE el nivel: la próxima vez insiste más (a la 4ª ya es la ventana del medio).
    const m = { ...leerEscalada(), [a.claim_id]: n + 1 };
    guardarEscalada(m); setNivel(m);
    api.post(`/api/coc/alerts/${a.claim_id}/dismiss`, {}).catch(() => {});
  };

  /** La respuesta del modal ES un follow-up de Claims: queda la huella (quién/cuándo/por qué) y
   *  el reloj del depto se re-arma solo → la alerta se calla hasta la próxima fecha. */
  async function responder() {
    if (!motivo.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/claims/${a.claim_id}/follow-ups`, {
        stage: 'claims', channel: 'note', department: 'claims',
        note: `COC not sent yet — ${motivo.trim()}`,
      });
      const m = leerEscalada(); delete m[a.claim_id]; guardarEscalada(m); setNivel(m);
      setMotivo('');
      setAlerts((prev) => prev.slice(1));
      setTotal((x) => Math.max(0, x - 1));
      api.post(`/api/coc/alerts/${a.claim_id}/dismiss`, {}).catch(() => {});
    } catch { /* si falla, la alerta sigue ahí — que insista */ }
    finally { setBusy(false); }
  }

  // ─── nivel 4+: la ventana en el medio — responder es más fácil que seguir cerrándola ───
  if (n >= NIVEL_VENTANA) {
    return (
      <>
        {/* Mientras el DRAWER del claim está abierto, el modal se ESCONDE (reporte Laura Chacón
            2026-08-19: "Send it now" abría el claim pero la pantalla seguía sombreada y nada era
            clickeable — el overlay del modal quedaba encima del drawer). Al cerrar el drawer,
            load() re-evalúa y si el COC ya salió, la alerta no vuelve. */}
        {!open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', padding: 16 }}>
          <div className="card" style={{ width: 'min(560px, 100%)', margin: 0, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-flex' }}><FileText size={20} strokeWidth={1.75} /></span>
              <b style={{ fontSize: 'var(--fs-base)' }}>{t('cocAlert.msg', { name: a.label, days: String(a.days) })}</b>
            </div>
            <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('cocAlert.whyTitle')}</div>
            <textarea className="input" autoFocus style={{ minHeight: 90, fontSize: 'var(--fs-sm)' }}
              placeholder={t('cocAlert.whyPh')} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn ghost sm" onClick={() => setOpen(a.claim_id)}>{t('cocAlert.send')} →</button>
              <span style={{ flex: 1 }} />
              {/* No hay "cerrar" a secas acá: o se manda el COC, o se explica por qué no. */}
              <button className="btn sm" style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }}
                disabled={busy || !motivo.trim()} onClick={() => { void responder(); }}>
                {busy ? '…' : t('cocAlert.answer')}
              </button>
            </div>
          </div>
        </div>
        )}
        {open && <ClaimDrawer claimId={open} initialTab="coc" onClose={() => { setOpen(null); load(); }} />}
      </>
    );
  }

  return (
    <>
      <div className="coc-banner" role="alert">
        <button className="coc-banner-main" onClick={() => setOpen(a.claim_id)}>
          <span className="coc-banner-tag"><FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cocAlert.tag')}</span>
          <span className="coc-banner-msg">
            {t('cocAlert.msg', { name: a.label, days: String(a.days) })}
          </span>
          {restantes > 0 && (
            <span className="coc-banner-more">{t('cocAlert.more', { n: String(restantes) })}</span>
          )}
          <span className="coc-banner-go">{t('cocAlert.send')} →</span>
        </button>
        <button className="coc-banner-x" onClick={dismiss} title={t('cocAlert.dismiss')}><X size={14} strokeWidth={1.75} /></button>
      </div>
      {/* Abre el claim ya parado en COC: el botón de enviar está ahí mismo. */}
      {open && <ClaimDrawer claimId={open} initialTab="coc" onClose={() => { setOpen(null); load(); }} />}
    </>
  );
}
